/**
 * Client-side validation helpers for branding asset file pickers.
 * Mirror the server's BrandingController.ValidateImageDataUrl rules so
 * users get instant feedback before the PUT round-trip.
 *
 * Rules (authoritative on server; these must stay in sync):
 *   lightLogo: PNG or JPEG, max 200 KB decoded
 *   favicon:   PNG or ICO,  max  32 KB decoded
 */

export type AssetKind = "lightLogo" | "favicon";

export interface AssetRule {
  accept: string;             // <input accept="…"> value
  mimeTypes: string[];        // allowed browser File.type values
  maxBytes: number;           // decoded byte limit
  label: string;              // human label for error messages
}

export const ASSET_RULES: Record<AssetKind, AssetRule> = {
  lightLogo: {
    accept: ".png,.jpg,.jpeg",
    mimeTypes: ["image/png", "image/jpeg"],
    maxBytes: 200 * 1024,
    label: "Logo",
  },
  favicon: {
    accept: ".png,.ico",
    mimeTypes: ["image/png", "image/x-icon", "image/vnd.microsoft.icon"],
    maxBytes: 32 * 1024,
    label: "Favicon",
  },
};

export type ValidationResult =
  | { ok: true; dataUrl: string; sizeKb: number }
  | { ok: false; error: string };

/**
 * Validate a browser File against an asset rule.
 * Returns the base64 data URL on success, or an error message on failure.
 *
 * All checks mirror the server so the client never accepts something the
 * server will reject:
 *   1. MIME type must be in the allowed list (type extension mapping).
 *   2. File.size must not exceed the decoded byte limit (same as decoded
 *      base64 payload; File.size == decoded byte count for binary files).
 *   3. FileReader reads the file as a data URL for the final preview/send.
 */
export async function validateAndReadAsset(
  file: File,
  rule: AssetRule,
): Promise<ValidationResult> {
  // 1. Type check — use file.type when available; fall back to extension.
  const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
  const effectiveMime =
    file.type ||
    (ext === "ico" ? "image/x-icon" : ext === "png" ? "image/png" : ext === "jpg" || ext === "jpeg" ? "image/jpeg" : "");

  if (!rule.mimeTypes.some((m) => m === effectiveMime)) {
    const allowed = rule.mimeTypes
      .map((m) => m.split("/")[1].replace("x-icon", "ico").replace("vnd.microsoft.icon", "ico"))
      .filter((v, i, a) => a.indexOf(v) === i)
      .join(", ");
    return { ok: false, error: `${rule.label}: unsupported file type. Allowed: ${allowed}.` };
  }

  // 2. Size check — File.size equals the raw byte count (same as decoded).
  if (file.size > rule.maxBytes) {
    const maxKb = rule.maxBytes / 1024;
    const actualKb = Math.ceil(file.size / 1024);
    return {
      ok: false,
      error: `${rule.label}: exceeds the ${maxKb} KB size limit (${actualKb} KB selected).`,
    };
  }

  // 3. Read as data URL.
  const dataUrl = await readFileAsDataUrl(file);
  return { ok: true, dataUrl, sizeKb: Math.ceil(file.size / 1024) };
}

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });
}
